import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { NavigationError, provideRouter, Router, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideTranslateLoader, provideTranslateService, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs';

import { routes } from './app.routes';
import { applyDocumentLang, resolveInitialLang } from './guards/language.guard';
import { LazyTranslateLoader } from './i18n/lazy-translate-loader';
import { languageInterceptor } from './interceptors/language.interceptor';
import { retryInterceptor } from './interceptors/retry.interceptor';
import { ssrCookieInterceptor } from './interceptors/ssr-cookie.interceptor';
import { timeoutInterceptor } from './interceptors/timeout.interceptor';
import { AnalyticsService } from './services/analytics.service';
import { ThemeService } from './services/theme.service';
import { SelectivePreloadingStrategy } from './utils/selective-preloading.strategy';
import { reloadOnceOnStaleChunk } from './utils/stale-chunk-reload';

const reloadPage = (): void => window.location.reload();

class StaleChunkErrorHandler implements ErrorHandler {
  private readonly sentryErrorHandler = Sentry.createErrorHandler();

  handleError(error: unknown): void {
    if (reloadOnceOnStaleChunk(error, reloadPage)) {
      return;
    }

    this.sentryErrorHandler.handleError(error);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withFetch(),
      withInterceptors([languageInterceptor, ssrCookieInterceptor, retryInterceptor, timeoutInterceptor]),
    ),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withPreloading(SelectivePreloadingStrategy),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
    ),
    provideClientHydration(withEventReplay()),
    provideTranslateService({ fallbackLang: 'fr', loader: provideTranslateLoader(LazyTranslateLoader) }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const platformId = inject(PLATFORM_ID);
      const document = inject(DOCUMENT);

      const initialLang = resolveInitialLang(platformId);
      applyDocumentLang(document, initialLang);
      return translate.use(initialLang);
    }),
    provideAppInitializer(() => {
      inject(ThemeService).init();
    }),
    provideAppInitializer(() => {
      inject(AnalyticsService).init();
    }),
    provideAppInitializer(() => {
      const router = inject(Router);
      const platformId = inject(PLATFORM_ID);

      if (!isPlatformBrowser(platformId)) {
        return;
      }

      router.events.pipe(filter((event) => event instanceof NavigationError)).subscribe((event) => {
        reloadOnceOnStaleChunk(event.error, reloadPage);
      });
    }),
    {
      provide: ErrorHandler,
      useClass: StaleChunkErrorHandler,
    },
    {
      deps: [Router],
      provide: Sentry.TraceService,
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
